/**
 * ContractDeactivateModal - Модальное окно деактивации/восстановления договора
 */
import React, { useState, useEffect } from 'react';
import {
  Modal,
  Textarea,
  Button,
  Group,
  Stack,
  Text,
  Alert,
  Box,
  Badge,
} from '@mantine/core';
import {
  IconX,
  IconCheck,
  IconFileText,
} from '@tabler/icons-react';
import tokens from '../../../theme/designTokens';

// Цвета статусов
const STATUS_COLORS = {
  draft: 'gray',
  pending_signature: 'yellow',
  signed: 'blue',
  active: 'green',
  paused: 'orange',
  terminated: 'red',
};

const ContractDeactivateModal = ({
  opened,
  onClose,
  onConfirm,
  contract,
  mode = 'deactivate', // 'deactivate' или 'restore'
  loading = false,
}) => {
  const [reason, setReason] = useState('');
  const [error, setError] = useState(null);

  // Сброс при открытии
  useEffect(() => {
    if (opened) {
      setReason('');
      setError(null);
    }
  }, [opened]);

  if (!contract) return null;

  const isDeactivate = mode === 'deactivate';
  const statusColor = STATUS_COLORS[contract.status] || 'gray';

  // Форматирование даты
  const formatDate = (dateStr) => {
    if (!dateStr) return null;
    return new Date(dateStr).toLocaleDateString('ru-RU', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    });
  };

  const handleSubmit = async () => {
    if (!reason.trim()) {
      setError('Укажите причину');
      return;
    }

    setError(null);
    try {
      await onConfirm(reason.trim());
    } catch (err) {
      setError(err.message || 'Произошла ошибка');
    }
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={isDeactivate ? 'Деактивировать договор' : 'Восстановить договор'}
      size="md"
      radius="xl"
      styles={{
        content: {
          borderRadius: '16px',
        },
      }}
    >
      <Stack gap="md">
        {/* Информация о договоре */}
        <Group
          p="sm"
          style={{
            borderRadius: '12px',
            backgroundColor: tokens.colors.neutral[1],
          }}
        >
          <Box
            style={{
              width: 40,
              height: 40,
              borderRadius: '10px',
              backgroundColor: '#e3f2fd',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <IconFileText size={20} color="#1976d2" />
          </Box>
          <Box style={{ flex: 1 }}>
            <Group gap="xs">
              <Text size="sm" fw={500}>
                {contract.display_name}
              </Text>
              <Badge
                size="xs"
                variant="light"
                radius="xl"
                color={statusColor}
              >
                {contract.status_display}
              </Badge>
            </Group>
            <Text size="xs" c="dimmed">
              Подписан: {formatDate(contract.signing_date)}
              {' • '}
              Начало: {formatDate(contract.start_date)}
            </Text>
            {contract.subjects && contract.subjects.length > 0 && (
              <Group gap={4} mt={4}>
                {contract.subjects.slice(0, 3).map((subject) => (
                  <Badge
                    key={subject.id}
                    size="xs"
                    variant="light"
                    radius="xl"
                    color="blue"
                  >
                    {subject.name}
                  </Badge>
                ))}
                {contract.subjects.length > 3 && (
                  <Text size="xs" c="dimmed">
                    +{contract.subjects.length - 3}
                  </Text>
                )}
              </Group>
            )}
          </Box>
        </Group>

        {/* Предупреждение */}
        <Alert
          icon={isDeactivate ? <IconX size={16} /> : <IconCheck size={16} />}
          color={isDeactivate ? 'orange' : 'green'}
          variant="light"
        >
          {isDeactivate ? (
            <>
              <Text size="sm" fw={500}>
                Договор будет деактивирован
              </Text>
              <Text size="xs" mt={4}>
                Он останется в системе, но будет помечен как неактивный.
                Вы сможете восстановить его позже.
              </Text>
            </>
          ) : (
            <>
              <Text size="sm" fw={500}>
                Договор будет восстановлен
              </Text>
              <Text size="xs" mt={4}>
                Он снова станет активным и доступным для использования.
              </Text>
            </>
          )}
        </Alert>

        {/* Причина */}
        <Textarea
          label={isDeactivate ? 'Причина деактивации' : 'Причина восстановления'}
          placeholder={
            isDeactivate
              ? 'Например: Договор расторгнут, истёк срок действия...'
              : 'Например: Ошибочная деактивация, договор возобновлён...'
          }
          required
          minRows={10}
          autosize
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          error={error}
        />

        {/* Кнопки */}
        <Group justify="flex-end">
          <Button variant="subtle" onClick={onClose}>
            Отмена
          </Button>
          <Button
            color={isDeactivate ? 'orange' : 'green'}
            leftSection={isDeactivate ? <IconX size={16} /> : <IconCheck size={16} />}
            onClick={handleSubmit}
            loading={loading}
          >
            {isDeactivate ? 'Деактивировать' : 'Восстановить'}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
};

export default ContractDeactivateModal;
